import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { CopyIdButton } from "@/components/admin/copy-id-button";
import { LicenseStatusBadge } from "@/components/admin/status";
import { useTimezone } from "@/lib/timezone";
import type { SearchResult } from "@/lib/types";
import { cn } from "@/lib/utils";

export interface SearchRow {
  id: string;
  type: "license";
  result: SearchResult;
}

export function rowSelectionKey(row: SearchRow) {
  return `${row.type}:${row.id}`;
}

export function SearchResultsTable({
  rows,
  selectedRowKey,
  onSelect,
}: {
  rows: SearchRow[];
  selectedRowKey?: string;
  onSelect: (row: SearchRow) => void;
}) {
  const { t } = useTranslation();

  const columns = [
    t("nav.licenses", "Licenses"),
    t("licenses.customer", "Customer"),
    t("licenses.product", "Product"),
    t("licenses.seats", "Seats"),
    t("licenses.validUntil", "Valid until"),
    t("common.status", "Status"),
  ];

  const selectedIndex = rows.findIndex(
    (row) => rowSelectionKey(row) === selectedRowKey,
  );

  function moveSelection(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const nextIndex = selectedIndex < 0
      ? 0
      : event.key === "ArrowDown"
        ? Math.min(rows.length - 1, selectedIndex + 1)
        : Math.max(0, selectedIndex - 1);
    const next = rows[nextIndex];
    if (next) onSelect(next);
  }

  return (
    <div
      className="h-full overflow-auto console-scrollbar"
      tabIndex={0}
      onKeyDown={moveSelection}
      role="region"
      aria-label={t("search.results", "Results")}
    >
      <table className="w-full min-w-[640px] border-collapse text-left text-xs">
        <caption className="sr-only">
          {t("search.table.licenseCaption", "License index records")}
        </caption>
        <thead className="sticky top-0 z-10 border-b border-line bg-panel text-xs text-muted">
          <tr>
            {columns.map((column) => (
              <th className="whitespace-nowrap px-4 py-2 font-medium" key={column} scope="col">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((row) => (
            <SearchResultsTableRow
              key={`${row.type}-${row.id}`}
              row={row}
              selected={rowSelectionKey(row) === selectedRowKey}
              onSelect={() => onSelect(row)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SearchResultsTableRow({
  row,
  selected,
  onSelect,
}: {
  row: SearchRow;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const { formatDate } = useTimezone();
  const alias = row.result.license.alias?.trim();
  const validUntil = row.result.license.valid_until;

  return (
    <tr
      className={cn(
        "cursor-pointer bg-panel hover:bg-panel-raised",
        selected && "bg-panel-raised shadow-[inset_1px_0_0_hsl(var(--accent))]",
      )}
      onClick={onSelect}
    >
      <th
        className="max-w-[220px] px-4 py-2 font-medium"
        scope="row"
        aria-current={selected ? "true" : undefined}
      >
        <div className="flex items-start gap-1">
          <button
            type="button"
            className="min-w-0 flex-1 text-left text-ink underline-offset-4 hover:text-accent hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus pointer-coarse:min-h-11"
            onClick={(event) => {
              event.stopPropagation();
              onSelect();
            }}
          >
            <span className="block truncate">{alias ?? row.result.license.id}</span>
            {alias && (
              <span className="block truncate font-mono text-[11px] font-normal text-muted">
                {row.result.license.id}
              </span>
            )}
          </button>
          <CopyIdButton
            value={row.result.license.id}
            label={t("nav.licenses", "License ID")}
          />
        </div>
      </th>
      <td className="max-w-[180px] truncate px-4 py-2 text-muted" title={row.result.customer.name}>
        {row.result.customer.name}
      </td>
      <td className="max-w-[180px] truncate px-4 py-2 text-muted" title={row.result.product.name}>
        {row.result.product.name}
      </td>
      <td className="whitespace-nowrap px-4 py-2 font-mono text-muted">
        {row.result.active_activation_count} / {row.result.license.policy.max_devices}
      </td>
      <td className="whitespace-nowrap px-4 py-2 text-muted">
        {validUntil
          ? formatDate(validUntil)
          : t("licenses.perpetual", "Perpetual")}
      </td>
      <td className="whitespace-nowrap px-4 py-2">
        <LicenseStatusBadge
          status={row.result.license.status}
          validUntil={row.result.license.valid_until}
        />
      </td>
    </tr>
  );
}
